import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from '../auth';
import { PageHeader } from '../components/page-elements';

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });

const RailcarUpload = () => {
  const user = useMemo(() => getStoredUser(), []);
  const navigate = useNavigate();
  const isInternal = user?.role === 'internal' || user?.role === 'admin';

  const [selectedFileName, setSelectedFileName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [result, setResult] = useState(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');
    setSuccess('');
    setResult(null);

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a .csv file');
      return;
    }

    try {
      const text = await readFileAsText(file);
      setSelectedFileName(file.name);
      setCsvText(text);
    } catch (err) {
      setError(err.message || 'Could not read file');
    }
  };

  const handleUpload = async () => {
    if (!csvText.trim()) {
      setError('Choose a CSV file before uploading.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    setResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/railcars/ingest/manual`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ csv: csvText }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to process railcar CSV');
      }

      setResult(payload);
      setSuccess(`Upload completed: ${payload.upserted || 0} railcars upserted.`);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isInternal) {
    return (
      <Box p={2}>
        <Alert severity="error">Access forbidden: insufficient rights.</Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <PageHeader
        title="Railcar Upload"
        subtitle="Upload the latest CSV extract to refresh railcar statuses, details, and active records."
        actions={
          <>
            <Button variant="outlined" onClick={() => navigate('/railcars')}>
              Back to Railcars
            </Button>
          </>
        }
      />

      <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Manual CSV Import
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Required CSV columns include fields that map to Car Initial, Car Number, Customer Code, status, and optional weight/station/track details.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="outlined" component="label">
                Choose CSV File
                <input type="file" accept=".csv,text/csv" hidden onChange={handleFileSelect} />
              </Button>
              {selectedFileName ? <Chip label={selectedFileName} color="primary" variant="outlined" /> : null}
            </Stack>

            <TextField
              label="CSV Preview"
              multiline
              minRows={8}
              maxRows={14}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder="CSV file content will appear here after you select a file."
              fullWidth
            />

            {error ? <Alert severity="error">{error}</Alert> : null}
            {success ? <Alert severity="success">{success}</Alert> : null}

            {result ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip color="info" label={`Received: ${result.received || 0}`} />
                <Chip color="success" label={`Upserted: ${result.upserted || 0}`} />
                <Chip color="warning" label={`Deactivated: ${result.deactivated || 0}`} />
                <Chip color="error" label={`Rejected: ${result.rejected || 0}`} />
              </Stack>
            ) : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="contained" onClick={handleUpload} disabled={submitting}>
                {submitting ? 'Uploading...' : 'Upload and Process'}
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setSelectedFileName('');
                  setCsvText('');
                  setError('');
                  setSuccess('');
                  setResult(null);
                }}
                disabled={submitting}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RailcarUpload;
